<script lang="ts">
  import { onMount } from "svelte";
  import { api } from "@/api";
  import {
    authenticate,
    checking as sessionChecking,
    retrySession,
    sessionRecoveryNeeded,
    sessionNotice,
  } from "@/stores/session";
  import Brand from "@/components/Brand.svelte";
  import TextInput from "@/components/TextInput.svelte";

  type AuthMode = "login" | "register";

  // Публичный site key; без него виджет не отображается (панель без TURNSTILE_SECRET проверку пропускает).
  const TURNSTILE_SITEKEY = (import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined) ?? "";

  let mode = $state<AuthMode>("login");
  let email = $state("");
  let username = $state("");
  let password = $state("");
  let showPassword = $state(false);
  let acceptedTerms = $state(false);
  let error = $state("");
  let submitting = $state(false);
  let turnstileToken = $state("");
  let captchaContainer = $state<HTMLDivElement | null>(null);
  let captchaWidgetId: string | null = null;

  function resetCaptcha() {
    turnstileToken = "";
    const ts = window.turnstile;
    if (ts && captchaWidgetId !== null) {
      ts.reset(captchaWidgetId);
    }
  }

  function renderCaptcha() {
    const ts = window.turnstile;
    if (!TURNSTILE_SITEKEY || !ts || !captchaContainer) {
      return;
    }
    if (captchaWidgetId !== null) {
      ts.reset(captchaWidgetId);
      return;
    }
    captchaWidgetId = ts.render(captchaContainer, {
      sitekey: TURNSTILE_SITEKEY,
      theme: "dark",
      callback: (token: string) => {
        turnstileToken = token;
      },
      "expired-callback": () => {
        turnstileToken = "";
      },
      "error-callback": () => {
        turnstileToken = "";
      },
    });
  }

  onMount(() => {
    window.onTurnstileLoad = renderCaptcha;
    if (window.turnstile) {
      renderCaptcha();
    }
  });

  function changeMode(nextMode: AuthMode) {
    mode = nextMode;
    showPassword = false;
    error = "";
    resetCaptcha();
  }

  function setLoginMode() {
    changeMode("login");
  }

  function setRegisterMode() {
    changeMode("register");
  }

  function togglePassword(event: MouseEvent) {
    event.preventDefault();
    showPassword = !showPassword;
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    submitting = true;
    error = "";
    try {
      const session =
        mode === "login"
          ? await api.login({ email, password, turnstileToken })
          : await api.register({ email, username, password, turnstileToken });
      authenticate(session);
    } catch (requestError) {
      error = (requestError as Error).message;
    } finally {
      submitting = false;
      resetCaptcha();
    }
  }
</script>

<svelte:head>
  {#if TURNSTILE_SITEKEY}
    <script
      src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit"
      async
      defer
    ></script>
  {/if}
</svelte:head>

<main class="auth-screen">
  <div class="auth-space" aria-hidden="true">
    <span class="auth-planet"></span>
    <span class="auth-orbit"></span>
  </div>

  <section class="auth-card" aria-labelledby="auth-title">
    <div class="auth-brand"><Brand /></div>
    <p class="eyebrow">Личная панель / {mode === "login" ? "вход" : "регистрация"}</p>
    <h1 id="auth-title">
      <span>{mode === "login" ? "С возвращением" : "Начни свою"}</span>
      &#32;
      <em>{mode === "login" ? "на орбиту" : "миссию"}</em>
    </h1>

    {#if $sessionNotice}<p role="status">{$sessionNotice}</p>{/if}

    {#if $sessionRecoveryNeeded}
      <div class="form-error" role="alert">
        Не удалось проверить сохранённую сессию. Повторите попытку или войдите в аккаунт.
      </div>
      <button
        class="button auth-submit session-retry"
        type="button"
        disabled={$sessionChecking}
        onclick={() => void retrySession()}
      >
        Повторить проверку сессии
      </button>
    {/if}

    <div class="auth-tabs" role="tablist" aria-label="Режим авторизации">
      <button
        type="button"
        role="tab"
        aria-selected={mode === "login"}
        class={mode === "login" ? "active" : ""}
        onclick={setLoginMode}
      >
        Войти
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "register"}
        class={mode === "register" ? "active" : ""}
        onclick={setRegisterMode}
      >
        Регистрация
      </button>
    </div>

    {#if error}
      <div class="form-error" role="alert">{error}</div>
    {/if}

    <form class="auth-form" onsubmit={submit}>
      {#if mode === "register"}
        <label>
          Имя пользователя
          <TextInput
            density="comfortable"
            name="username"
            autocomplete="username"
            minlength={3}
            maxlength={32}
            pattern="[a-zA-Z0-9_.-]+"
            bind:value={username}
            placeholder="mars_pilot"
            required
          />
        </label>
      {/if}
      <label>
        Email
        <TextInput
          density="comfortable"
          type="email"
          name="email"
          autocomplete="email"
          bind:value={email}
          placeholder="you@example.com"
          required
        />
      </label>
      <label>
        Пароль
        <div class="password-input-wrap">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            autocomplete={mode === "login" ? "current-password" : "new-password"}
            minlength={8}
            maxlength={128}
            bind:value={password}
            placeholder="Минимум 8 символов"
            required
          />
          <button
            type="button"
            class="toggle-password-btn"
            onclick={togglePassword}
            aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
            title={showPassword ? "Скрыть пароль" : "Показать пароль"}
          >
            {#if showPassword}
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                <path
                  d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
                />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                <line x1="2" x2="22" y1="2" y2="22" />
              </svg>
            {:else}
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            {/if}
          </button>
        </div>
      </label>
      {#if mode === "register"}
        <label class="terms-consent">
          <input type="checkbox" bind:checked={acceptedTerms} required />
          <span>
            Я принимаю
            <a href="https://mars-host.ru/terms" target="_blank" rel="noreferrer"
              >пользовательское соглашение</a
            >
            и
            <a href="https://mars-host.ru/privacy" target="_blank" rel="noreferrer"
              >политику конфиденциальности</a
            >
          </span>
        </label>
      {/if}
      <div class="captcha" bind:this={captchaContainer}></div>
      <button
        class="button auth-submit"
        type="submit"
        disabled={submitting ||
          (!!TURNSTILE_SITEKEY && !turnstileToken) ||
          (mode === "register" && !acceptedTerms)}
      >
        {submitting
          ? "Устанавливаем связь…"
          : mode === "login"
            ? "Войти в панель"
            : "Создать аккаунт"}
        <span aria-hidden="true">↗</span>
      </button>
    </form>
  </section>
</main>

<style>
  .auth-brand {
    margin-bottom: clamp(3rem, 8vh, 5rem);
  }

  .auth-screen {
    position: relative;
    display: grid;
    min-height: 100svh;
    overflow: hidden;
    padding: 3rem 1rem;
    background:
      radial-gradient(circle at 82% 36%, rgb(214 75 50 / 0.17), transparent 31rem), var(--ink-deep);
    place-items: center;
  }

  .auth-screen::before {
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(1px 1px at 15% 20%, rgb(241 225 198 / 0.55), transparent),
      radial-gradient(1px 1px at 70% 15%, rgb(241 225 198 / 0.5), transparent),
      radial-gradient(1px 1px at 45% 70%, rgb(240 112 79 / 0.55), transparent),
      radial-gradient(1px 1px at 90% 80%, rgb(241 225 198 / 0.55), transparent),
      radial-gradient(1px 1px at 28% 86%, rgb(241 225 198 / 0.35), transparent);
    content: "";
    pointer-events: none;
  }

  .auth-space {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .auth-planet {
    position: absolute;
    top: 27%;
    right: clamp(-17rem, -10vw, -8rem);
    width: clamp(24rem, 34vw, 38rem);
    aspect-ratio: 1;
    border-radius: 50%;
    background: var(--mars);
    box-shadow: 0 0 100px rgb(214 75 50 / 0.25);
  }

  .auth-orbit {
    position: absolute;
    top: 18%;
    right: clamp(-20rem, -12vw, -9rem);
    width: clamp(31rem, 46vw, 50rem);
    aspect-ratio: 1;
    border: 1px solid rgb(214 75 50 / 0.4);
    border-radius: 50%;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    width: min(100%, 30rem);
    padding: clamp(1.5rem, 4vw, 2.4rem);
    border: 1px solid var(--line-strong);
    background: rgb(17 17 19 / 0.9);
    box-shadow: 0 35px 110px rgb(0 0 0 / 0.48);
    backdrop-filter: blur(18px);
  }

  .auth-card h1 {
    display: grid;
    gap: 0.12em;
    max-width: 100%;
    margin: 1rem 0 1.9rem;
    font: 500 clamp(1.55rem, 6.5vw, 2.12rem)/1.08 var(--font-display);
    letter-spacing: -0.075em;
    text-transform: uppercase;
  }

  .auth-card h1 span,
  .auth-card h1 em {
    display: block;
    max-width: 100%;
  }

  .auth-card h1 span {
    white-space: nowrap;
  }

  .auth-card h1 em {
    color: var(--mars-light);
    font-style: normal;
  }

  .auth-tabs {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid var(--line);
  }

  .auth-tabs button {
    padding: 0 0 0.75rem;
    border: 0;
    border-bottom: 1px solid transparent;
    color: var(--dim);
    background: transparent;
    font-size: 0.76rem;
    transition:
      color 160ms ease,
      border-color 160ms ease;
  }

  .auth-tabs button:hover,
  .auth-tabs button.active {
    color: var(--cream);
  }

  .auth-tabs button.active {
    border-color: var(--mars-light);
  }

  .auth-form {
    display: grid;
    gap: 1.15rem;
  }

  .auth-form label {
    display: grid;
    gap: 0.48rem;
    color: var(--muted);
    font-size: 0.66rem;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  .auth-form input {
    width: 100%;
    min-height: 3rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid var(--line);
    border-radius: 0;
    outline: 0;
    color: var(--cream);
    background: var(--ink);
    font-size: 0.86rem;
    text-transform: none;
    transition:
      border-color 160ms ease,
      box-shadow 160ms ease;
  }

  .auth-form input::placeholder {
    color: var(--dim);
  }

  .auth-form input:focus {
    border-color: var(--mars-light);
    box-shadow: 0 0 0 3px rgb(214 75 50 / 0.08);
  }

  .password-input-wrap {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  .password-input-wrap input {
    padding-right: 2.75rem;
  }

  .toggle-password-btn {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--dim);
    cursor: pointer;
    transition: color 160ms ease;
  }

  .toggle-password-btn:hover {
    color: var(--cream);
  }

  .toggle-password-btn:focus-visible {
    color: var(--mars-light);
    outline: none;
  }

  .auth-submit {
    width: 100%;
    margin-top: 0.35rem;
  }

  .auth-submit:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }

  .captcha {
    display: flex;
    justify-content: center;
  }

  .terms-consent {
    display: flex !important;
    grid-template-columns: none !important;
    align-items: flex-start;
    gap: 0.65rem !important;
    color: var(--muted) !important;
    font-size: 0.68rem !important;
    letter-spacing: 0 !important;
    line-height: 1.5;
    text-transform: none !important;
  }

  .terms-consent input {
    flex: 0 0 auto;
    width: 1rem;
    min-height: 1rem;
    margin: 0.12rem 0 0;
    accent-color: var(--mars-light);
  }

  .terms-consent a {
    color: var(--cream);
    text-decoration-color: rgb(240 112 79 / 0.55);
    text-underline-offset: 0.18em;
  }

  .terms-consent a:hover {
    color: var(--mars-light);
  }

  @media (max-width: 700px) {
    .auth-planet {
      right: -18rem;
      opacity: 0.55;
    }

    .auth-orbit {
      right: -22rem;
      opacity: 0.65;
    }
  }

  @media (max-width: 390px) {
    .auth-card :global(.brand-name) {
      display: inline;
    }

    .auth-card h1 {
      font-size: 1.43rem;
    }
  }
</style>
